import React, { useRef, useState, useEffect } from "react";
import { Flex, Button, Select, Divider, Space } from "antd";
import "./A2zEditor.css";

const templates = {
  abdomen: `findings:
Liver: [normal size and attenuation]
Gallbladder: [no gallstones]
Spleen: [normal size and attenuation]
Pancreas: [normal size and attenuation]
Stomach: [no masses or wall thickening]
Small Bowel: [no dilatation or wall thickening]
Colon: [no masses or wall thickening]
Kidneys: [normal size and attenuation]
Adrenal glands: [normal size and attenuation]
Abdominal Aorta: [no aneurysm]
Mesenteric vessels: [normal caliber and flow]
Peritoneal cavity: [no free fluid or abnormal masses]
`,
  pelvis: `findings:
Bladder: [normal wall thickness]
Prostate: [normal size and attenuation]
Seminal Vesicles: [no abnormalities]
Uterus: [normal size and contour]
Endometrium: [normal thickness]
Ovaries: [no masses or cysts]
Fallopian Tubes: [normal appearance]
Rectum: [no masses or wall thickening]
Rectosigmoid Colon: [normal caliber and wall thickness]
Peritoneal Cavity: [no free fluid or abnormal masses]
`,
  abdomen_pelvis: `findings:
Liver: [normal size and attenuation]
Bladder: [normal wall thickness]
Spleen: [normal size and attenuation]
Pancreas: [normal size and attenuation]
Stomach: [no masses or wall thickening]
Small Bowel: [no dilatation or wall thickening]
Colon: [no masses or wall thickening]
Kidneys: [normal size and attenuation]
Adrenal glands: [normal size and attenuation]
Abdominal Aorta: [no aneurysm]
Mesenteric vessels: [normal caliber and flow]
Peritoneal cavity: [no free fluid or abnormal masses]
Prostate: [normal size and attenuation]
Seminal Vesicles: [no abnormalities]
Uterus: [normal size and contour]
Endometrium: [normal thickness]
Ovaries: [no masses or cysts]
Fallopian Tubes: [normal appearance]
Rectum: [no masses or wall thickening]
Rectosigmoid Colon: [normal caliber and wall thickness]
`,
};

export default function A2zEditor() {
  // const saveButtonRef = useRef(null);
  // const templateSelectorRef = useRef(null);
  const textAreaRef = useRef(null);
  const lineNumbersRef = useRef(null);
  const [selectedTemplate, setSelectedTemplate] = useState(
    Object.keys(templates)[0]
  );
  const [formattedContent, setFormattedContent] = useState("");
  const [lineNumbers, setLineNumbers] = useState([]);
  // const [currentIndex, setCurrentIndex] = useState(0); // State to keep track of the current index
  const [currentBracketIndex, setCurrentBracketIndex] = useState(0);

  // Update line numbers whenever the formatted content changes
  useEffect(() => {
    setLineNumbers(
      [...Array(formattedContent.split("\n").length).keys()].map((x) => x)
    );
  }, [formattedContent]); // Depend on formattedContent to update line numbers

  /**
   * Updates the formatting of the provided HTML content.
   * - Applies 'level0' class to the 'findings' main category.
   * - Applies 'level1' class with indentation to all sub-items.
   * - Wraps text within brackets with the 'brackets' class.
   *
   * @param {string} htmlContent - The original HTML content to format.
   * @returns {string} The formatted HTML content.
   */
  const updateFormatting = (htmlContent) => {
    console.log("original content:");
    console.log(htmlContent);

    // Split the entire HTML content into individual lines.
    // Example Input: "findings:\nLiver: [normal size and attenuation]\nGallbladder: [no gallstones]"
    let text = htmlContent
      .split("\n")
      .map((line) => {
        // Check if the current line is the main category 'findings'.
        if (line.includes("findings:")) {
          // If it is, return the line as is without further changes.
          // Example Output for 'findings': "<span class="level0">findings</span>:"
          return `<span class="level0">findings</span>:`;
        } else {
          // If the line is not 'findings', format sub-items as level1 with four spaces of indentation.
          // This regex captures any text preceding a colon and wraps it in a <span> with class "level1".
          // Matches strings like "Liver" in "Liver: [normal size and attenuation]"
          // Wraps "Liver" with <span class="level1">, resulting in "  <span class="level1">Liver</span>:"
          // Transforms "Liver: [normal size and attenuation]" into "  <span class="level1">Liver</span>: [normal size and attenuation]"
          return line.replace(
            /([^:\n]+):/g,
            '    <span class="level1">$1</span>:'
          );
        }
      })
      .join("\n"); // Joins all lines back into a single string with newline characters.

    // Add span tags around content inside brackets across all lines.
    // This regex identifies text within square brackets and wraps it with a span with class "brackets".
    // Matches strings like "[normal size and attenuation]"
    // Wraps the content within the brackets, resulting in "<span class="brackets">[normal size and attenuation]</span>"
    // Example: For "Liver: [normal size and attenuation]", after regex it becomes "Liver: <span class="brackets">[normal size and attenuation]</span>"
    text = text.replace(/\[(.+?)\]/g, '<span class="brackets">[$1]</span>');

    console.log("formatted content:");
    console.log(text);
    return text;
  };

  // // Save button click handler
  // const handleSave = () => {
  //   console.log("save button clicked", saveButtonRef.current);
  // };

  // Next button click handler
  const handleNextBtn = () => {
    handleNavigation("next");
  };

  // Previous button click handler
  const handlePrevBtn = () => {
    handleNavigation("prev");
  };

  // Initialize formatted content on component mount and on template change
  useEffect(() => {
    if (selectedTemplate) {
      const initialContent = templates[selectedTemplate] || "";
      const formattedContent = updateFormatting(initialContent);
      setFormattedContent(formattedContent);

      // Reset currentBracketIndex to 0
      setCurrentBracketIndex(-1);

      // Clear all existing green background colors from brackets
      setTimeout(() => {
        const brackets = textAreaRef.current.querySelectorAll(".brackets");
        brackets.forEach((bracket) => {
          bracket.style.backgroundColor = ""; // Clear the background color
        });
      }, 0);
    }
  }, [selectedTemplate]); // Depend on selectedTemplate to update formatted content

  const handleNavigation = (direction) => {
    const brackets = textAreaRef.current.querySelectorAll(".brackets");
    if (brackets.length > 0) {
      // Clear background color from all brackets
      brackets.forEach((bracket) => {
        bracket.style.backgroundColor = "";
      });

      let newIndex = currentBracketIndex;
      if (direction === "next") {
        // If 'next', increment the index; wraps around to 0 if it goes out of bounds
        newIndex = (currentBracketIndex + 1) % brackets.length;
      } else if (direction === "prev") {
        // If 'prev' and it's the first operation (currentBracketIndex == -1), go to the last element
        if (currentBracketIndex === -1) {
          newIndex = brackets.length - 1;
        } else {
          // Otherwise, decrement the index; wraps around to the last if it goes below 0
          newIndex =
            (currentBracketIndex - 1 + brackets.length) % brackets.length;
        }
      }

      // Set the newly calculated index as the current index
      setCurrentBracketIndex(newIndex);

      // Set background color of the new current bracket
      brackets[newIndex].style.backgroundColor = "orangered";
    }
  };

  const handleTemplateChange = (value) => {
    setSelectedTemplate(value);
  };

  return (
    <>
      <div>
        <div>
          <h1>a2z Editor</h1>
          <Space direction="horizontal" size="middle">
            <Button
              href="http://18.208.133.128/viewer?StudyInstanceUIDs=1.3.6.1.4.1.5962.1.1.0.0.0.1194734704.16302.0.1"
              target="_blank"
              rel="noopener noreferrer"
            >
              DICOM Viewer
            </Button>
            <span>|</span>
            {/* <Button ref={saveButtonRef} onClick={handleSave}>
              Save
            </Button> */}
            <Button onClick={handlePrevBtn}>Prev</Button>
            <Button onClick={handleNextBtn}>Next</Button>
            <Select
              onChange={handleTemplateChange}
              value={selectedTemplate}
              style={{ width: 200 }}
              options={Object.keys(templates).map((template) => ({
                value: template,
                label: template.replace(/_/g, " ").toUpperCase(),
              }))}
            />
          </Space>
          <Divider />
        </div>
        <Flex gap="small" style={{lineHeight: '1.5', width: '100%'}}>
          <div ref={lineNumbersRef}>
            {lineNumbers.map((lineNumber) => (
              <div key={lineNumber}>{lineNumber}</div>
            ))}
          </div>
          <div
            ref={textAreaRef}
            contentEditable="true"
            dangerouslySetInnerHTML={{ __html: formattedContent }}
            style={{ whiteSpace: "pre-wrap", border: 'none', outline: 'none'}}
          />
        </Flex>
      </div>
    </>
  );
}
